"use client";

import { useEffect, useState } from "react";
import { signInWithGoogle, ensureSignedIn } from "@/lib/platform/firebase";
import { createRazorpaySubscription, createPaypalSubscription, BillingError } from "@/lib/platform/api";
import { usePlan } from "@/lib/platform/usePlan";
import { trackEvent } from "@/lib/analytics/track";

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => { open: () => void };
  }
}

function loadRazorpayScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay checkout"));
    document.body.appendChild(script);
  });
}

export default function UpgradeToPro() {
  const { isPro, loading: planLoading } = usePlan();
  const [signedIn, setSignedIn] = useState(false);
  const [isIndia, setIsIndia] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ensureSignedIn().then((u) => setSignedIn(!u.isAnonymous));
    setIsIndia(Intl.NumberFormat().resolvedOptions().locale.toLowerCase().includes("in"));
  }, []);

  useEffect(() => {
    // Only a real paywall impression once we know the plan and it isn't
    // already Pro — this is what actually shows the upgrade buttons below,
    // not just "this component mounted."
    if (!planLoading && !isPro) trackEvent("payment_page_open", { flow: "pro_subscription" });
  }, [planLoading, isPro]);

  async function handleSignIn() {
    setError(null);
    setBusy(true);
    try {
      const user = await signInWithGoogle();
      setSignedIn(!user.isAnonymous);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRazorpay() {
    trackEvent("payment_button_click", { flow: "pro_subscription", gateway: "razorpay" });
    setError(null);
    setBusy(true);
    try {
      await loadRazorpayScript();
      const { subscription_id, key_id } = await createRazorpaySubscription();
      trackEvent("try_to_pay", { flow: "pro_subscription", gateway: "razorpay", subscriptionId: subscription_id });
      new window.Razorpay!({
        key: key_id,
        subscription_id,
        name: "Nohaya Pro",
        description: "₹799/mo — unlocks Creator Pro voices, AI Tools, and more",
        handler: () => window.location.reload(),
      }).open();
    } catch (e) {
      setError(e instanceof BillingError ? e.message : "Couldn't start checkout.");
    } finally {
      setBusy(false);
    }
  }

  async function handlePaypal() {
    trackEvent("payment_button_click", { flow: "pro_subscription", gateway: "paypal" });
    setError(null);
    setBusy(true);
    try {
      const { subscription_id, approve_url } = await createPaypalSubscription();
      if (approve_url) {
        trackEvent("try_to_pay", { flow: "pro_subscription", gateway: "paypal", subscriptionId: subscription_id });
        window.location.href = approve_url;
      } else throw new BillingError("No approval URL returned.");
    } catch (e) {
      setError(e instanceof BillingError ? e.message : "Couldn't start checkout.");
      setBusy(false);
    }
  }

  if (planLoading) return null;

  if (isPro) {
    return (
      <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
        You're on Nohaya Pro — enjoy unlimited access.
      </p>
    );
  }

  return (
    <div>
      {!signedIn ? (
        <button
          type="button"
          onClick={handleSignIn}
          disabled={busy}
          className="w-full rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-slate-900"
        >
          Sign in with Google to subscribe
        </button>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleRazorpay}
            disabled={busy}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold text-white transition disabled:opacity-50 ${
              isIndia ? "bg-rose-600 hover:bg-rose-700" : "bg-slate-700 hover:bg-slate-800"
            }`}
          >
            Pay ₹799/mo with Razorpay (India)
          </button>
          <button
            type="button"
            onClick={handlePaypal}
            disabled={busy}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold text-white transition disabled:opacity-50 ${
              !isIndia ? "bg-rose-600 hover:bg-rose-700" : "bg-slate-700 hover:bg-slate-800"
            }`}
          >
            Pay $9.99/mo with PayPal (International)
          </button>
        </div>
      )}
      {error ? <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">{error}</p> : null}
    </div>
  );
}
